import { Stack } from "@mui/material";
import {
  type MerchantDetailDto,
  ProductCode,
  type MerchantAppProductDto,
} from "Infrastructure/Api/Api";
import { useAppProductsList } from "Hooks/Options/useAppProductsList";
import { ProductAccordion } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/ProductAccordion";
import { isComgateProductCode } from "Components/Shared/Merchant/Products/AppProducts/constants";
import type { ProductPricingsType } from "Components/Hunter/Merchants/types";
import type { AppProductsVariant } from "Components/Shared/Merchant/Products/AppProducts/AppProducts";
import type {
  GetPrerequisites,
  PrerequisiteItem,
} from "Components/Shared/Merchant/Products/AppProducts/types";
import { Resources, useResource } from "Translations/Resources";

const PREREQUISITE_RESOURCES =
  Resources.Components.Merchant.AppProducts.Form.Prerequisites;

type Props = {
  products: Array<MerchantAppProductDto>;
  merchantPublicID: string;
  variant: AppProductsVariant;
  merchantData: MerchantDetailDto;
  allGroupedPricings: ProductPricingsType;
  getPrerequisites?: GetPrerequisites;
  isOnboarding?: boolean;
};

export const AppProductsContent = ({
  merchantPublicID,
  products,
  merchantData,
  allGroupedPricings,
  variant,
  getPrerequisites,
  isOnboarding,
}: Props) => {
  const { t } = useResource();
  const { products: availableProducts } = useAppProductsList();

  const getBuiltInPrerequisites = (
    code: ProductCode,
  ): Array<PrerequisiteItem> => {
    if (isComgateProductCode(code) && !isOnboarding) {
      return [
        {
          text: t(PREREQUISITE_RESOURCES.FacilityAdded),
          isCompleted: merchantData.facilities.length > 0,
        },
      ];
    }
    return [];
  };

  return (
    <Stack gap={2}>
      {availableProducts.map(({ code }) => {
        const isDefault = code === ProductCode.OrbitS;
        const isActive = products.some((p) => p.productDto.code === code);

        return (
          <ProductAccordion
            key={code}
            code={code}
            isActive={isActive}
            isDefault={isDefault}
            isDisabled={code === ProductCode.OrbitM}
            variant={variant}
            merchantPublicID={merchantPublicID}
            merchantData={merchantData}
            allGroupedPricings={allGroupedPricings}
            isOnboarding={isOnboarding}
            prerequisites={[
              ...getBuiltInPrerequisites(code),
              ...(getPrerequisites?.(code) ?? []),
            ]}
          />
        );
      })}
    </Stack>
  );
};
